import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Globals } from '../global/globals';

export interface FirstStagePaper {
  id: number;
  title: string;
  abstractField: string;
  publicationDate: string;
  venue?: string;
  workType?: string;
  sourceType?: string;
}

export interface ResearcherProgress {
  userId: number;
  firstName: string;
  lastName: string;
  role: string;
  completedCount: number;
  totalCount: number;
}

export interface FirstStageScreeningStats {
  totalPapers: number;
  unscreenedPapers: number;
  partiallyScreenedPapers: number;
  includedPapers: number;
  excludedPapers: number;
  /** Only present for PI; null/undefined for regular researchers. */
  teamTotalDecisions?: number;
  teamTotalRequiredDecisions?: number;
  teamIncludedPapers?: number;
  teamExcludedPapers?: number;
  researcherProgress?: ResearcherProgress[];
}

@Injectable({
  providedIn: 'root',
})
export class FirstStageScreeningService {
  constructor(
    private httpClient: HttpClient,
    private globals: Globals,
  ) {}

  private getBaseUri(projectId: string): string {
    return `${this.globals.backendUri}/projects/${projectId}/first-stage-screening`;
  }

  getNextPaper(projectId: string): Observable<FirstStagePaper | null> {
    return this.httpClient.get<FirstStagePaper | null>(`${this.getBaseUri(projectId)}/next`);
  }

  getCurrentPaper(projectId: string): Observable<FirstStagePaper | null> {
    return this.httpClient.get<FirstStagePaper | null>(`${this.getBaseUri(projectId)}/current`);
  }

  submitVote(projectId: string, paperId: number, include: boolean): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/${paperId}/vote`, {
      include,
    });
  }

  getStats(projectId: string): Observable<FirstStageScreeningStats> {
    return this.httpClient.get<FirstStageScreeningStats>(`${this.getBaseUri(projectId)}/stats`);
  }

  unlockPaper(projectId: string, paperId: number): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/${paperId}/unlock`, {});
  }

  skipPaper(projectId: string, paperId: number): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/${paperId}/skip`, {});
  }

  deleteVote(projectId: string, paperId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.getBaseUri(projectId)}/${paperId}/vote`);
  }

  advanceToFullTextRetrieval(projectId: string): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/advance`, {});
  }
}
